import { useState } from 'react';
import API from '../../Services/api';

const rewardTypes = [
  { value: 'discount', label: 'Discount', icon: 'fas fa-tag', desc: 'Percentage or fixed discount at a partner store' },
  { value: 'voucher', label: 'Voucher / Gift Card', icon: 'fas fa-ticket-alt', desc: 'Redeemable store voucher or gift card' },
  { value: 'experience', label: 'Eco Experience', icon: 'fas fa-leaf', desc: 'Event, activity or green experience' },
  { value: 'donation', label: 'Donation', icon: 'fas fa-seedling', desc: 'Charitable or environmental donation' },
];

function AddReward({ onSuccess }) {
  const [formData, setFormData] = useState({
    type: 'discount',
    description: '',
    pointsRequired: '',
    expiryDate: '',
  });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await API.post('/rewards/add', formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setMessage('Reward added successfully!');
      setFormData({ type: 'discount', description: '', pointsRequired: '', expiryDate: '' });
      if (onSuccess) setTimeout(onSuccess, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding reward. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = rewardTypes.find(t => t.value === formData.type);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden max-w-2xl">
      <div className="bg-gradient-to-r from-[#3d0f6b] to-[#6b21a8] px-6 py-5">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <i className="fas fa-plus-circle"></i>
          Add New Reward
        </h2>
        <p className="text-purple-200 text-sm mt-1">Create a reward for recycling programme participants</p>
      </div>

      <div className="p-6">
        {message && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 mb-6">
            <i className="fas fa-check-circle text-green-500"></i>
            <span className="text-sm font-medium">{message}</span>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6">
            <i className="fas fa-exclamation-circle text-red-500"></i>
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Reward Type <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {rewardTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, type: type.value })}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                    formData.type === type.value
                      ? 'border-[#3d0f6b] bg-purple-50 text-[#3d0f6b]'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <i className={`${type.icon} text-xl`}></i>
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
            {selectedType && (
              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                <i className={selectedType.icon}></i>
                {selectedType.desc}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description <span className="text-red-500">*</span></label>
            <textarea
              name="description"
              placeholder="e.g. 10% off your next purchase at EcoStore"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d0f6b]/30 focus:border-[#3d0f6b] transition-colors resize-none"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Points Required <span className="text-red-500">*</span></label>
              <div className="relative">
                <input
                  type="number"
                  name="pointsRequired"
                  placeholder="e.g. 500"
                  value={formData.pointsRequired}
                  onChange={handleChange}
                  min="1"
                  className="w-full pl-4 pr-12 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d0f6b]/30 focus:border-[#3d0f6b] transition-colors"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">pts</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Expiry Date <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d0f6b]/30 focus:border-[#3d0f6b] transition-colors text-gray-600"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-[#3d0f6b] text-white hover:bg-[#2d0b52] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
          >
            {loading ? (
              <><i className="fas fa-spinner fa-spin"></i> Adding...</>
            ) : (
              <><i className="fas fa-plus"></i> Add Reward</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddReward;
